import { useState, useEffect } from 'react';
import { getCompatibleBillingPlans } from '@/services/billing';
import type { BillingPlan, PlanCompatibilityInfo } from '@/types/billing';
import type { RadioFormData } from '../types';

interface PlanSelectionProps {
  formData: RadioFormData;
  updateField: (field: keyof RadioFormData, value: string) => void;
  errors: Record<string, string>;
}

function getPlanFeatureLabelsForSelection(plan: BillingPlan): string[] {
  if (plan.slug === 'free') {
    return ['Perfil profesional', 'Stream URL', 'Cabina básica', 'Redes sociales', `${plan.storage_gb} GB almacenamiento`];
  }
  const labels: string[] = [];
  if (plan.has_domain) labels.push('Dominio propio');
  labels.push(`${plan.storage_gb} GB almacenamiento`);
  if (plan.has_lives) labels.push('Lives y transmisiones');
  if (plan.has_app) labels.push('App personalizada');
  if (plan.has_managed_streaming) labels.push('Streaming RFW incluido');
  if (plan.has_autodj) labels.push('AutoDJ');
  return labels;
}

export default function PlanSelection({ formData, updateField, errors }: PlanSelectionProps) {
  const [compatiblePlans, setCompatiblePlans] = useState<PlanCompatibilityInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const selectedPlan = compatiblePlans.find((c) => c.plan.id === formData.planId)?.plan;
  const isFree = formData.planId !== '' && selectedPlan?.price_monthly === 0;
  const isManaged = formData.streamingMode === 'RFW_MANAGED';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCompatibleBillingPlans(formData.streamingMode)
      .then((plans) => {
        if (!cancelled) setCompatiblePlans(plans);
      })
      .catch(() => {
        if (!cancelled) setCompatiblePlans([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [formData.streamingMode]);

  return (
    <div className="space-y-4">
      {isManaged && (
        <div className="bg-accent-50/50 border border-accent-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-cloud-line text-accent-600 text-sm"></i>
            </div>
            <div>
              <p className="text-sm font-bold text-foreground-800 mb-1">
                Streaming administrado por RFW
              </p>
              <p className="text-xs text-foreground-500 leading-relaxed">
                Con esta modalidad, RadioFrecuenciaWeb aprovisiona tu estación de streaming. Los planes Pro, Business y Premium incluyen la capacidad de streaming administrado con AutoDJ, almacenamiento según el plan y soporte operativo.{' '}
                <strong>El plan Free no incluye streaming administrado.</strong>
              </p>
            </div>
          </div>
        </div>
      )}

      {errors.planId && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <p className="text-xs font-semibold text-red-600">{errors.planId}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="flex flex-col items-center gap-2">
            <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-xs text-foreground-400">Cargando planes...</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {compatiblePlans.map(({ plan, compatible, reason }) => {
            const selected = formData.planId === plan.id;
            const isHl = plan.slug === 'pro';
            const isPlanFree = plan.price_monthly === 0;
            const hasStreaming = plan.has_managed_streaming;
            const isDisabled = isManaged && !compatible;
            const featureLabels = getPlanFeatureLabelsForSelection(plan);

            return (
              <button
                key={plan.id}
                type="button"
                disabled={isDisabled}
                onClick={() => updateField('planId', plan.id)}
                className={`relative rounded-2xl p-5 text-left cursor-pointer transition-all duration-200 border-2 ${
                  isDisabled
                    ? 'opacity-50 cursor-not-allowed border-background-200 bg-background-50'
                    : selected
                      ? isHl
                        ? 'border-primary-500 bg-primary-50/30 ring-2 ring-primary-200 scale-[1.03] md:scale-105 z-10'
                        : plan.slug === 'business' || plan.slug === 'premium'
                        ? 'border-accent-500 bg-accent-50/30 ring-2 ring-accent-200'
                        : 'border-secondary-500 bg-secondary-50/30 ring-2 ring-secondary-200'
                      : 'border-background-200 bg-background-50 hover:border-background-300 hover:shadow-md'
                }`}
              >
                {plan.slug === 'pro' && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap bg-primary-500 text-background-50">
                    Más popular
                  </div>
                )}
                {plan.slug === 'business' && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap bg-accent-500 text-background-50">
                    Equipos
                  </div>
                )}
                {plan.slug === 'premium' && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap bg-accent-500 text-background-50">
                    Profesional
                  </div>
                )}

                <h3 className="text-base font-bold text-foreground-950 mb-0.5 mt-1">{plan.name}</h3>
                <p className="text-[10px] text-foreground-400 mb-3">
                  {plan.description.slice(0, 60)}...
                </p>

                <div className="flex items-baseline gap-0.5 mb-3">
                  {isPlanFree ? (
                    <span className="text-2xl font-extrabold text-foreground-950">Gratis</span>
                  ) : (
                    <span className="text-2xl font-extrabold text-foreground-950">{plan.formatted_price_monthly}</span>
                  )}
                </div>

                {/* Streaming compatibility badge */}
                {isManaged && (
                  <div
                    className={`flex items-center gap-1.5 mb-3 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold ${
                      hasStreaming
                        ? 'bg-accent-50 text-accent-700 border border-accent-200'
                        : 'bg-red-50 text-red-600 border border-red-200'
                    }`}
                  >
                    <i className={`${hasStreaming ? 'ri-check-line text-accent-600' : 'ri-close-line text-red-400'} text-xs`}></i>
                    {hasStreaming ? 'Streaming incluido' : 'Sin streaming administrado'}
                  </div>
                )}

                {/* Activation badge */}
                <div
                  className={`flex items-center gap-1.5 mb-3 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold ${
                    isPlanFree
                      ? 'bg-amber-50 text-amber-700 border border-amber-200'
                      : 'bg-green-50 text-green-700 border border-green-200'
                  }`}
                >
                  <i className={`${isPlanFree ? 'ri-time-line' : 'ri-flashlight-line'} text-xs`}></i>
                  {isPlanFree ? 'Sujeto a aprobación' : 'Activación inmediata'}
                </div>

                {isDisabled && reason && (
                  <p className="text-[10px] text-red-500 font-medium mb-2">{reason}</p>
                )}

                <ul className="space-y-1.5 mb-0">
                  {featureLabels.map((f) => (
                    <li key={f} className="flex items-start gap-1.5">
                      <i className="ri-check-line text-green-600 text-[10px] mt-0.5 shrink-0"></i>
                      <span className="text-[11px] text-foreground-600">{f}</span>
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>
      )}

      {selectedPlan && (
        <div
          className={`rounded-xl p-4 border ${
            isFree ? 'bg-amber-50/70 border-amber-200' : 'bg-green-50/70 border-green-200'
          }`}
        >
          <div className="flex items-start gap-3">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                isFree ? 'bg-amber-200' : 'bg-green-200'
              }`}
            >
              <i
                className={`text-sm ${isFree ? 'ri-time-line text-amber-700' : 'ri-flashlight-line text-green-700'}`}
              ></i>
            </div>
            <div>
              <p className="text-sm font-bold text-foreground-800 mb-0.5">
                {isFree ? 'Sujeto a aprobación' : 'Activación inmediata con revisión posterior'}
              </p>
              <p className="text-xs text-foreground-500 leading-relaxed">
                {isFree
                  ? 'Tu radio será revisada por nuestro equipo. El proceso toma entre 24 y 48 horas. Te notificaremos por correo cuando esté activa.'
                  : 'Redirigiremos al checkout seguro para completar el pago. Una vez confirmado, tu radio estará lista al instante.'}
                {isManaged && !isFree && (
                  <span className="block mt-1 text-accent-600 font-semibold">
                    <i className="ri-cloud-line mr-1"></i>
                    Incluye aprovisionamiento de streaming por RFW
                  </span>
                )}
              </p>
              <p className="text-[10px] text-foreground-400 mt-2 pt-2 border-t border-foreground-200/40">
                <i className="ri-information-line mr-1"></i>
                En todos los planes, tu <strong className="text-foreground-500">Frecuencia RFW se asigna automáticamente</strong>. Las frecuencias especiales se solicitan después desde tu panel.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}